<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    participantName: string;
    modelValue: number;
    supported?: boolean;
  }>(),
  {
    supported: true,
  },
);

const emit = defineEmits<{
  "update:modelValue": [volumePercent: number];
}>();

const rootElement = ref<HTMLElement | null>(null);
const triggerElement = ref<HTMLButtonElement | null>(null);
const sliderElement = ref<HTMLInputElement | null>(null);
const popoverOpen = ref(false);
const componentId = useId();
const popoverId = `${componentId}-popover`;
const sliderId = `${componentId}-slider`;

const normalizedModelVolume = computed(() => {
  if (!Number.isFinite(props.modelValue)) return 100;
  return Math.min(100, Math.max(0, Math.round(props.modelValue)));
});
const draftVolume = ref(normalizedModelVolume.value);

watch(normalizedModelVolume, (volumePercent) => {
  draftVolume.value = volumePercent;
});

const volumeIcon = computed(() => {
  if (!props.supported) return "mdi-volume-off";
  if (draftVolume.value === 0) return "mdi-volume-mute";
  if (draftVolume.value < 50) return "mdi-volume-low";
  return "mdi-volume-high";
});

const triggerLabel = computed(() =>
  props.supported
    ? `Ajustar volume de ${props.participantName}: ${draftVolume.value}%`
    : `Volume individual indisponível para ${props.participantName}`,
);

const closePopover = (restoreFocus = false) => {
  if (!popoverOpen.value) return;
  popoverOpen.value = false;
  if (restoreFocus) {
    nextTick(() => triggerElement.value?.focus());
  }
};

const togglePopover = async () => {
  popoverOpen.value = !popoverOpen.value;
  if (popoverOpen.value && props.supported) {
    await nextTick();
    sliderElement.value?.focus();
  }
};

const updateVolume = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const nextVolume = Number(input.value);
  if (!Number.isFinite(nextVolume)) return;
  draftVolume.value = Math.min(100, Math.max(0, nextVolume));
  emit("update:modelValue", draftVolume.value);
};

const handleOutsidePointerDown = (event: PointerEvent) => {
  if (
    popoverOpen.value &&
    event.target instanceof Node &&
    !rootElement.value?.contains(event.target)
  ) {
    closePopover();
  }
};

onMounted(() => {
  document.addEventListener("pointerdown", handleOutsidePointerDown);
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", handleOutsidePointerDown);
});
</script>

<template>
  <div ref="rootElement" class="participant-volume-control">
    <button
      ref="triggerElement"
      type="button"
      class="participant-volume-control__trigger"
      :class="{
        'participant-volume-control__trigger--unsupported': !supported,
      }"
      :aria-label="triggerLabel"
      :title="triggerLabel"
      aria-haspopup="dialog"
      :aria-expanded="popoverOpen"
      :aria-controls="popoverOpen ? popoverId : undefined"
      @click="togglePopover"
      @keydown.esc.prevent.stop="closePopover(true)"
    >
      <i class="mdi" :class="volumeIcon" aria-hidden="true" />
    </button>

    <div
      v-if="popoverOpen"
      :id="popoverId"
      class="participant-volume-control__popover"
      role="dialog"
      :aria-label="`Volume de ${participantName}`"
      @keydown.esc.prevent.stop="closePopover(true)"
    >
      <template v-if="supported">
        <div class="participant-volume-control__heading">
          <label :for="sliderId">Volume</label>
          <output :for="sliderId">{{ draftVolume }}%</output>
        </div>
        <input
          :id="sliderId"
          ref="sliderElement"
          type="range"
          min="0"
          max="100"
          step="5"
          :value="draftVolume"
          :aria-label="`Volume de ${participantName}`"
          @input="updateVolume"
        />
      </template>
      <p v-else role="status">
        O controle individual de volume não está disponível neste dispositivo.
      </p>
    </div>
  </div>
</template>

<style scoped lang="scss">
.participant-volume-control {
  position: relative;
  flex: 0 0 auto;
}

.participant-volume-control__trigger {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 12px;
  background: rgba(17, 24, 39, 0.76);
  color: #fff;
  cursor: pointer;
  transition:
    border-color 160ms ease,
    background-color 160ms ease;

  i {
    font-size: 1.12rem;
  }

  &:hover,
  &[aria-expanded="true"] {
    border-color: rgba(255, 255, 255, 0.46);
    background: rgba(31, 42, 68, 0.94);
  }

  &:focus-visible {
    outline: 3px solid rgba(141, 166, 255, 0.9);
    outline-offset: 2px;
  }
}

.participant-volume-control__trigger--unsupported {
  color: #c9d2e2;
}

.participant-volume-control__popover {
  position: absolute;
  z-index: 4;
  right: 0;
  bottom: calc(100% + 8px);
  width: 208px;
  max-width: calc(100vw - 48px);
  padding: 11px 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.96);
  box-shadow: 0 12px 30px rgba(5, 10, 24, 0.34);
  color: #fff;

  input[type="range"] {
    display: block;
    width: 100%;
    height: 28px;
    margin: 5px 0 0;
    accent-color: #8da6ff;
    cursor: pointer;

    &:focus-visible {
      outline: 3px solid rgba(141, 166, 255, 0.72);
      outline-offset: 2px;
    }
  }

  p {
    margin: 0;
    color: #dbe4f2;
    font-size: 0.72rem;
    line-height: 1.4;
  }
}

.participant-volume-control__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.72rem;

  label {
    font-weight: 700;
  }

  output {
    color: #dbe4f2;
    font-variant-numeric: tabular-nums;
  }
}

@media (prefers-reduced-motion: reduce) {
  .participant-volume-control__trigger {
    transition: none;
  }
}
</style>
